import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { PluginStore } from "graylog-web-plugin/plugin";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { getPathnameWithoutId } from "util/URLUtils";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import { Row, Col, Modal, Input, SegmentedControl } from "components/bootstrap";
import { ModalSubmit, Spinner, Select } from "components/common";
import useSelectedIndexSetTemplate from "components/indices/IndexSetTemplates/hooks/useSelectedTemplate";
import useBuiltInTemplates from "components/indices/IndexSetTemplates/hooks/useBuiltInTemplates";
import useTemplates from "components/indices/IndexSetTemplates/hooks/useTemplates";
import TemplateDetails from "components/indices/IndexSetTemplates/TemplateDetails";
import IndexSetTemplateCard from "components/indices/IndexSetTemplates/IndexSetTemplateCard";
import { DATA_TIERING_TYPE } from "components/indices/data-tiering";
const FlexWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.md};
  `
);
const SelectIndexSetTemplateModal = ({ hideModal, show }) => {
  var _a;
  const { selectedIndexSetTemplate, setSelectedIndexSetTemplate } = useSelectedIndexSetTemplate();
  const [tempSelectedTemplate, setTempSelectedTemplate] = useState(
    selectedIndexSetTemplate
  );
  const initialTemplateCategory = selectedIndexSetTemplate && !selectedIndexSetTemplate.built_in ? "custom" : "built_in";
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState(initialTemplateCategory);
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const telemetryPathName = useMemo(() => getPathnameWithoutId(pathname), [pathname]);
  const dataTieringPlugin = PluginStore.exports("dataTiering").find(
    (plugin) => plugin.type === DATA_TIERING_TYPE.HOT_WARM
  );
  const [showBuiltInWarmTier, setShowBuiltInWarmTier] = useState(!!dataTieringPlugin);
  const templateCategorySegments = [
    { value: "built_in", label: "Built-in Templates" },
    { value: "custom", label: "Custom Templates" }
  ];
  const { isLoading: isLoadingBuiltIn, data: builtInList } = useBuiltInTemplates(showBuiltInWarmTier);
  const {
    isLoading: isLoadingCustom,
    data: { list: customList }
  } = useTemplates({
    page: 1,
    pageSize: 20,
    query: "built_in:false",
    sort: {
      attributeId: "title",
      direction: "asc"
    }
  });
  const trackSelectedTemplate = () => {
    let template_name = "custom";
    if (tempSelectedTemplate.built_in) {
      template_name = tempSelectedTemplate.title;
    }
    sendTelemetry(TELEMETRY_EVENT_TYPE.INDEX_SET_TEMPLATE.SELECTED, {
      app_pathname: telemetryPathName,
      app_action_value: "select-index-set-template-submitted",
      template_name
    });
  };
  const handleTabClick = (category) => {
    setTempSelectedTemplate(void 0);
    setSelectedTemplateCategory(category);
  };
  const handleSubmit = () => {
    trackSelectedTemplate();
    setSelectedIndexSetTemplate(tempSelectedTemplate);
    hideModal();
  };
  const handleCardClick = (template) => {
    setTempSelectedTemplate(template);
  };
  const handleCustomSelect = (selectedTemplateId) => {
    const templateToSelect = customList.find((template) => template.id === selectedTemplateId);
    if (templateToSelect) {
      setTempSelectedTemplate(templateToSelect);
    }
  };
  const handleClose = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INDEX_SET_TEMPLATE.SELECT_CLOSED, {
      app_pathname: telemetryPathName,
      app_action_value: "select-index-set-template-cancelled"
    });
    hideModal();
  };
  const selectedCustomTemplate = customList.find((template) => template.id === (tempSelectedTemplate == null ? void 0 : tempSelectedTemplate.id));
  return /* @__PURE__ */ React.createElement(Modal, { show, bsSize: "large", onHide: handleClose }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Index Set Templates")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(FlexWrapper, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, "Select a template appropriate to the requirements for this data (and available storage).", ((_a = tempSelectedTemplate == null ? void 0 : tempSelectedTemplate.index_set_config.data_tiering) == null ? void 0 : _a.warm_tier_enabled) && dataTieringPlugin && /* @__PURE__ */ React.createElement(dataTieringPlugin.WarmTierReadinessInfo, null))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    SegmentedControl,
    {
      data: templateCategorySegments,
      value: selectedTemplateCategory,
      onChange: handleTabClick
    }
  ))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, selectedTemplateCategory === "built_in" && /* @__PURE__ */ React.createElement(FlexWrapper, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "built-in-data-tiering",
      type: "checkbox",
      label: "Warm Tier (Enterprise)",
      checked: showBuiltInWarmTier,
      onChange: () => setShowBuiltInWarmTier(!showBuiltInWarmTier)
    }
  ), isLoadingBuiltIn ? /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Spinner, null)) : builtInList.map((template) => /* @__PURE__ */ React.createElement(
    IndexSetTemplateCard,
    {
      template,
      handleCardClick,
      key: template.id,
      isSelected: (tempSelectedTemplate == null ? void 0 : tempSelectedTemplate.id) === template.id
    }
  ))), selectedTemplateCategory === "custom" && (isLoadingCustom ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(FlexWrapper, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    Select,
    {
      clearable: false,
      onChange: handleCustomSelect,
      options: customList.map((template) => ({ label: template.title, value: template.id })),
      placeholder: "Select a template",
      value: selectedCustomTemplate == null ? void 0 : selectedCustomTemplate.id
    }
  ))), selectedCustomTemplate && /* @__PURE__ */ React.createElement(TemplateDetails, { template: selectedCustomTemplate, showDescription: true }))))))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onSubmit: handleSubmit,
      submitButtonType: "button",
      disabledSubmit: !tempSelectedTemplate,
      submitButtonText: "Apply template",
      displayCancel: false
    }
  )));
};
export default SelectIndexSetTemplateModal;
